/**
 * Catálogos de idioma sob demanda: pt-BR entra embutido e é o fallback de todo caminho degradado;
 * os outros cinco entram por `import()`, um chunk cada. A leitura (`getCatalog`) é síncrona e devolve
 * o cache de módulo, que o `ensureCatalog` do bootstrap enche antes de a árvore React montar.
 */
import Cookie from 'js-cookie';

import { COOKIE_LANG } from '../../constants/Common';

import * as ptBR from '../../app/lang/pt-BR';
import { TLangFile } from './Lang.type';

/** Valor do cookie de idioma → catálogo. As chaves são as que o produto grava no cookie. */
export type TLangKey = 'pt_br' | 'en_us' | 'es' | 'fr' | 'it' | 'de';

/** Idioma do catálogo embutido. Todo caminho degradado termina aqui. */
export const FALLBACK_LANG: TLangKey = 'pt_br';

const FALLBACK_CATALOG = ptBR as TLangFile;

/*
 * Um `import()` literal por locale, não um template com variável: o especificador estático é o
 * que faz o bundler emitir um chunk por catálogo e registrá-lo como entrada própria no
 * manifest — que é o contrato que a Issue 09 (preload) vai consumir.
 */
const LOADERS: Record<Exclude<TLangKey, 'pt_br'>, () => Promise<TLangFile>> = {
    en_us: () => import('../../app/lang/en-US'),
    es: () => import('../../app/lang/es-AR'),
    fr: () => import('../../app/lang/fr-FR'),
    it: () => import('../../app/lang/it-IT'),
    de: () => import('../../app/lang/de-DE')
};

/** Cache de módulo. Já nasce com o embutido — daí pt-BR nunca disparar `import()`. */
const catalogs: Partial<Record<TLangKey, TLangFile>> = { [FALLBACK_LANG]: FALLBACK_CATALOG };

/** Carga em voo, para duas chamadas concorrentes não baixarem o mesmo catálogo duas vezes. */
const pending: Partial<Record<TLangKey, Promise<void>>> = {};

const hasOwn = (obj: object, key: string): boolean => Object.prototype.hasOwnProperty.call(obj, key);

/** `true` só para os seis idiomas que o produto grava no cookie. */
export const isKnownLang = (lang?: string): lang is TLangKey =>
    lang === FALLBACK_LANG || (!!lang && hasOwn(LOADERS, lang));

/**
 * Idioma efetivo: o que foi pedido ou, na ausência, o do cookie. Locale desconhecido vira o
 * fallback — nunca `undefined`, nunca exceção.
 *
 * Omitir o argumento tem de significar "o idioma ativo do usuário", não "pt-BR": os
 * consumidores (o HOC de tradução, entre eles) só passam idioma quando estão forçando um.
 */
export const normalizeLang = (lang?: string): TLangKey => {
    const requested = lang || Cookie.get(COOKIE_LANG);

    return isKnownLang(requested) ? requested : FALLBACK_LANG;
};

/**
 * Registra a falha sem prender o boot. O `import()` do APM é feito só aqui, no caminho de erro:
 * importar o APM estaticamente traria o `initApm` como efeito colateral para dentro de todo
 * bundle que usa idioma. `console.error` sozinho não bastaria — o terser derruba `console` no
 * build de produção.
 */
const reportFailure = (lang: TLangKey, error: unknown): void => {
    // eslint-disable-next-line no-console
    console.error(`[lang] falha ao carregar o catálogo "${lang}", seguindo em ${FALLBACK_LANG}`, error);

    import('../APM')
        .then(({ default: apm }) => apm.captureError(error as Error))
        .catch(() => { /* telemetria é best-effort: se ela cair junto, o app segue */ });
};

/** `true` quando `getCatalog(lang)` já devolve o catálogo real, sem esperar por rede. */
export const isCatalogReady = (lang?: string): boolean => hasOwn(catalogs, normalizeLang(lang));

/** Leitura síncrona. Enquanto o catálogo do locale não chegou, devolve o embutido. */
export const getCatalog = (lang?: string): TLangFile =>
    catalogs[normalizeLang(lang)] ?? FALLBACK_CATALOG;

/**
 * Bootstrap: garante o catálogo do locale em memória antes de a tela existir.
 *
 * **Nunca rejeita.** Falhou o `import()`, resolve mesmo assim e o app monta em pt-BR — este
 * `await` está no caminho crítico do boot, e uma rejeição aqui seria a tela branca.
 */
export const ensureCatalog = (lang?: string): Promise<void> => {
    const key = normalizeLang(lang);

    if (isCatalogReady(key)) return Promise.resolve();

    if (!pending[key]) {
        pending[key] = LOADERS[key as Exclude<TLangKey, 'pt_br'>]()
            .then((catalog) => { catalogs[key] = catalog; })
            .catch((error) => { reportFailure(key, error); });
    }

    return pending[key] as Promise<void>;
};

/* Dispara a carga junto com o boot, não no primeiro componente que pede idioma. No-op em pt-BR. */
ensureCatalog();
